import { Role, TokenClaims } from "./auth";

/** Which roles have any admin surface at all. Godown QC agents and
 * customers manage nothing in the portal. */
export const ADMIN_ROLES: Role[] = [
  "agrilens_super",
  "enterprise_admin",
  "state_head",
  "district_head",
];

export function canSeeAdmin(claims: TokenClaims | null): boolean {
  return !!claims && ADMIN_ROLES.includes(claims.role);
}

/** The landing route for the "Admin" nav link, keyed to the caller's
 * role + scope. Super gets the enterprise directory; a scoped head
 * jumps straight to the one scope they manage.
 *
 * Returns null for roles with no admin surface. For scoped roles
 * with no matching scope in the JWT (shouldn't happen, but be
 * defensive), falls back to the generic dashboard root "/".
 */
export function adminRootPath(claims: TokenClaims | null): string | null {
  if (!claims) return null;
  switch (claims.role) {
    case "agrilens_super":
      return "/admin/enterprises";
    case "enterprise_admin": {
      const id = firstScopeId(claims, "enterprise") ?? claims.enterprise_id;
      return id ? `/admin/enterprise/${id}` : "/";
    }
    case "state_head": {
      const id = firstScopeId(claims, "state");
      return id ? `/admin/state/${id}` : "/";
    }
    case "district_head": {
      const id = firstScopeId(claims, "district");
      return id ? `/admin/district/${id}` : "/";
    }
    default:
      return null;
  }
}

function firstScopeId(
  claims: TokenClaims,
  type: "enterprise" | "state" | "district" | "godown",
): number | null {
  // `scopes` is normalised to an array in decodeClaims, but guard here
  // too so this never throws even if called with a hand-built claims
  // object — a throw here unmounts the whole app (called from Layout).
  const s = (claims.scopes ?? []).find((x) => x.scope_type === type);
  return s ? s.scope_id : null;
}
